"use client";

import Link from "@/components/ui/Link";
import { useEffect, useState } from "react";
import { sanitizeHTML } from "@/lib/utils";

interface PageSectionNavigationData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  anchors?: {
    anchor_id: {
      anchor_id: string;
      label: string;
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",
  "subheading.text",
  "subheading.title",
  "anchors.anchor_id.anchor_id",
  "anchors.anchor_id.label",
] as const;

interface PageSectionNavigationProps {
  data: PageSectionNavigationData;
}

export default function PageSectionNavigation({
  data
}: PageSectionNavigationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [selectedSection, setSelectedSection] = useState<string>("");

  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector("header");
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    const setScrollMargin = () => {
      const anchors = document.querySelectorAll('[id]');
      anchors.forEach(anchor => {
        (anchor as HTMLElement).style.scrollMarginTop = '140px';
      });
    };

    updateHeaderHeight();
    setScrollMargin();

    window.addEventListener("resize", () => {
      updateHeaderHeight();
      setScrollMargin();
    });
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, []);

  return (
		<section
			id='page-section-navigation'
			className='w-full sticky top-[73px] md:top-[94px] z-[99]'
		>
			<div id='anchors' className='relative w-full'>
				<div className='w-full'>
					<button
						onClick={() => setIsOpen(!isOpen)}
						className='w-full bg-black cursor-pointer'
					>
						<div className='px-5 md:px-0 py-5 w-full'>
							<div className='flex flex-col md:flex-row md:items-center md:justify-between w-full container mx-auto'>
								<span className='text-white text-lg font-medium text-left'>
									{data?.heading?.title ? (
										<div
											dangerouslySetInnerHTML={sanitizeHTML(data.heading.title)}
										/>
									) : (
										'JUMP TO SECTION:'
									)}
								</span>
								<div className='md:flex items-center'>
									<div className='text-white md:hidden text-lg w-fit relative pr-6'>
										{selectedSection ||
											(data?.heading?.text ? (
												<div
													dangerouslySetInnerHTML={sanitizeHTML(
														data.heading.text
													)}
												/>
											) : (
												'Select Section'
											))}
										<span className='absolute right-0 top-1/2 -mt-1 border-[6px] border-transparent border-t-white'></span>
									</div>
									<div className='hidden md:flex items-center text-white text-sm font-medium'>
										{isOpen ? 'Hide sections' : 'Show sections'}
										<span
											className={`ml-2 transition-transform duration-200 ${
												isOpen ? 'rotate-180' : ''
											}`}
										>
											<svg
												width='12'
												height='8'
												viewBox='0 0 12 8'
												fill='none'
												xmlns='http://www.w3.org/2000/svg'
											>
												<path
													d='M1 1L6 6L11 1'
													stroke='currentColor'
													strokeWidth='2'
													strokeLinecap='round'
												/>
											</svg>
										</span>
									</div>
								</div>
							</div>
						</div>
					</button>
					<div
						className={`${
							isOpen ? 'block' : 'hidden'
						} left-0 top-full bg-black md:bg-white p-6 md:p-0 min-w-[285px] w-full z-10 transition-all duration-300`}
					>
						<div className='container mx-auto'>
							<ul className='space-y-2 md:space-y-0 md:grid md:grid-rows-5 md:grid-flow-col md:auto-cols-max xl:grid-rows-4 md:gap-x-16 md:gap-y-4 md:border-b md:border-black/20 md:py-6 md:justify-start'>
								{data?.anchors?.map((link, index) => (
									<li key={index} className='w-full block'>
										<Link
											href={link.anchor_id.anchor_id}
											onClick={() => {
												setSelectedSection(link.anchor_id.label)
												setIsOpen(false)
												const element = document.getElementById(
													link.anchor_id.anchor_id
												)
												if (element) {
													element.scrollIntoView({ behavior: 'smooth' })
												}
											}}
											className='text-white md:text-black hover:text-[#988a7d] text-base block mb-2 transition-colors duration-200 md:flex md:items-center'
										>
											<span className='hidden md:inline-block mr-2'>
												{index + 1}.
											</span>
											<div
												dangerouslySetInnerHTML={sanitizeHTML(
													link.anchor_id.label
												)}
											/>
										</Link>
									</li>
								))}
							</ul>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}
